/**
 * Storage access guard for privacy-hardened browsers.
 *
 * Firefox's "block all cookies"/locked-down mode (and some tracking-protection
 * extensions) throw `SecurityError: The operation is insecure` for several
 * storage-partitioned APIs. The one that actually crashes this app is
 * `new BroadcastChannel(...)`: Nuxt's `useCookie` constructs a BroadcastChannel
 * on EVERY client-side call (to sync cookie changes across tabs - see
 * node_modules/nuxt/dist/app/composables/cookie.js), so the first `useCookie`
 * during hydration throws, hydration crashes, and the user is bounced to the 500
 * error page a split second after the SSR HTML paints - on every page/locale.
 *
 * Cookie WRITES (`document.cookie = ...`) and Web Storage method calls throw in
 * the same mode, so we neutralise all of them:
 *   - BroadcastChannel: if construction is blocked, swap in a no-op stub (cookies
 *     still work in-page, only cross-tab sync is lost - acceptable when blocked).
 *   - document.cookie: delegate to the native getter/setter, falling back to "" /
 *     a no-op when native access throws (real cookies keep working for everyone else).
 *   - localStorage / sessionStorage: if their methods throw, swap in an in-memory shim.
 *
 * Runs with enforce: "pre" so it executes before any plugin, store or middleware
 * touches one of these APIs.
 */
export default defineNuxtPlugin({
  name: "storage-guard",
  enforce: "pre",
  setup() {
    guardBroadcastChannel();
    guardCookie();
    guardWebStorage("localStorage");
    guardWebStorage("sessionStorage");
  },
});

function guardBroadcastChannel(): void {
  try {
    const Native = window.BroadcastChannel;
    if (typeof Native !== "function") return;

    let blocked = false;
    try {
      new Native("storage-guard-probe").close();
    } catch {
      blocked = true;
    }
    if (!blocked) return;

    // Construction is blocked - replace with a no-op stub that satisfies the
    // BroadcastChannel interface Nuxt relies on (postMessage / close / onmessage).
    class SafeBroadcastChannel extends EventTarget {
      readonly name: string;
      onmessage: ((event: MessageEvent) => void) | null = null;
      onmessageerror: ((event: MessageEvent) => void) | null = null;
      constructor(name: string) {
        super();
        this.name = name;
      }
      postMessage(): void {}
      close(): void {}
    }
    window.BroadcastChannel = SafeBroadcastChannel as unknown as typeof BroadcastChannel;
  } catch (error) {
    console.warn("[storage-guard] Failed to guard BroadcastChannel:", error);
  }
}

function guardCookie(): void {
  try {
    const native = Object.getOwnPropertyDescriptor(Document.prototype, "cookie");
    const nativeGet = native?.get;
    const nativeSet = native?.set;
    if (!nativeGet || !nativeSet || native?.configurable === false) return;

    Object.defineProperty(document, "cookie", {
      configurable: true,
      get() {
        try {
          return nativeGet.call(document);
        } catch {
          return "";
        }
      },
      set(value) {
        try {
          nativeSet.call(document, value);
        } catch {
          // The browser blocks cookies; ignore the write so hydration does not crash.
        }
      },
    });
  } catch (error) {
    console.warn("[storage-guard] Failed to wrap document.cookie:", error);
  }
}

function guardWebStorage(name: "localStorage" | "sessionStorage"): void {
  try {
    // Property access can succeed while method calls still throw, so probe a method.
    window[name].getItem("storage-guard-probe");
    return; // usable - leave the native implementation in place
  } catch {
    // fall through and install an in-memory fallback
  }

  // Intentionally NON-persistent: this only installs when the browser already blocks
  // storage (degraded mode), so values live in memory for the page session and are not
  // saved across loads/tabs. Do not rely on it for real persistence (e.g. persisted
  // Pinia state) - it is purely a no-throw fallback to keep hydration alive.
  const store = new Map<string, string>();
  const shim = {
    getItem: (key: string) => (store.has(key) ? store.get(key)! : null),
    setItem: (key: string, value: string) => {
      store.set(String(key), String(value));
    },
    removeItem: (key: string) => {
      store.delete(key);
    },
    clear: () => {
      store.clear();
    },
    key: (index: number) => Array.from(store.keys())[index] ?? null,
    get length() {
      return store.size;
    },
  } as Storage;

  // The native accessor may live on the window instance or its prototype; try both
  // so we can shadow whichever one is configurable.
  for (const target of [window, Object.getPrototypeOf(window)]) {
    try {
      Object.defineProperty(target, name, { configurable: true, get: () => shim });
      return;
    } catch {
      // try the next target
    }
  }
  console.warn(`[storage-guard] Failed to install ${name} fallback.`);
}
